// Reads and validates the "where do we send the user back to" value: it
// arrives in the `redirect` query string (written by `ProfileParser`) and
// later becomes a `window.location` assignment here.

const STORAGE_KEY = 'redirect';

// A URL interpolated from a missing value arrives as the literal text.
const PLACEHOLDERS = new Set(['null', 'undefined']);

// Schemes that execute rather than navigate. Everything else stays allowed:
// mobile returns through a custom scheme deep link (`zippedi://...`).
const EXECUTABLE_SCHEME = /^\s*(javascript|data|vbscript|blob|file):/i;

// The WHATWG URL parser strips tab/LF/CR from anywhere in the string, not
// only the leading edge, before it looks at the scheme — so `java\tscript:`
// still executes even though it doesn't match a prefix-anchored regex. Strip
// the same characters here first so the check sees what the browser sees.
const STRIPPED_CONTROL_CHARS = /[\t\n\r\0]/g;

// An absolute http(s) redirect is only ever legitimate toward Zippedi's own
// domain (Heimdal itself, or the app that sent the user here); a loopback
// host is additionally allowed under http so local development keeps working.
const ALLOWED_HOST = /(^|\.)zippedi\.com$/i;
const LOOPBACK_HOST = /^(localhost|127\.0\.0\.1|10\.0\.2\.2)$/i;

// Any input is resolved against this placeholder: if the result still lands
// on it, the input carried no scheme/host of its own (a relative path, or
// `//`-relative would have overridden the host) and can only ever navigate
// within this document — always safe, whatever the path is.
const PLACEHOLDER_BASE = 'https://heimdal.invalid/';
const PLACEHOLDER_ORIGIN = new URL(PLACEHOLDER_BASE).origin;

function isAllowedTarget(url) {
  let resolved;
  try {
    resolved = new URL(url, PLACEHOLDER_BASE);
  } catch {
    return false;
  }
  if (resolved.origin === PLACEHOLDER_ORIGIN) return true;
  // zippedi.com is never served over plain http in any real environment, so
  // that exception stays https-only; loopback (local dev) is fine either way.
  if (resolved.protocol === 'https:') {
    return ALLOWED_HOST.test(resolved.hostname) || LOOPBACK_HOST.test(resolved.hostname);
  }
  if (resolved.protocol === 'http:') {
    return LOOPBACK_HOST.test(resolved.hostname);
  }
  // A non-executable custom scheme — already cleared the check above —
  // navigates through the OS rather than this document, e.g. `zippedi://...`
  // for a mobile return. Safe to allow through.
  return true;
}

/**
 * Rejects any URL whose scheme would execute rather than navigate, and any
 * absolute http(s)/`//`-relative URL that would leave Zippedi's domain.
 * Shared by every entry point that hands a value to `window.location` — the
 * stored return above and the `?redirect=` query param the login flow reads.
 */
export function isSafeRedirectUrl(url) {
  if (typeof url !== 'string') return false;
  const cleaned = url.replace(STRIPPED_CONTROL_CHARS, '');
  if (EXECUTABLE_SCHEME.test(cleaned)) return false;
  return isAllowedTarget(cleaned);
}

/** The stored destination, or `null` when nothing usable was ever stored. */
export function readReturnUrl() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (typeof stored !== 'string') return null;
  const url = stored.trim().replace(STRIPPED_CONTROL_CHARS, '');
  if (!url || PLACEHOLDERS.has(url.toLowerCase())) return null;
  return isSafeRedirectUrl(url) ? url : null;
}

/** Drops the stored destination once it has been navigated to. */
export function clearReturnUrl() {
  localStorage.removeItem(STORAGE_KEY);
}

// `replace` so a back gesture cannot return to a page whose session was just
// re-minted, and the app root so no click ever ends on a blank document.
export function returnToApp() {
  const url = readReturnUrl();
  clearReturnUrl();
  window.location.replace(url || '/');
}
